/**
 * Hero Franchise Section Mapper
 * Maps Content API data to renderable format
 */
import type {HeroSliderImage} from "@/sections/content/hero/parts/hero-slider";

import type {ContentApiTrustLogo, HeroFranchiseSectionData} from "./type";

/**
 * Mapped hero franchise data for rendering
 */
export interface HeroFranchiseMappedData {
    imageUrl: string;
    imageAlt: string;
    titleHtml: string;
    heroText?: string;
    badgeIconUrl?: string;
    badgeIconAlt?: string;
    sliderImages: Array<HeroSliderImage>;
    anchorId?: string;
    link?: {
        url: string;
        text: string;
        target: "_self" | "_blank";
    };
}

/**
 * Map trust logo to slider image
 */
function mapTrustLogoToSliderImage(logo: ContentApiTrustLogo): HeroSliderImage | null {
    const logoUrl = logo.logo_image?.url;

    if (!logoUrl) {
        return null;
    }

    return {
        src: logoUrl,
        alt: logo.logo_image?.alt || "",
        width: 150,
        height: 80,
    };
}

/**
 * Map hero franchise section data to renderable format
 */
export function mapHeroFranchiseData(data: HeroFranchiseSectionData): HeroFranchiseMappedData | null {
    const imageUrl = data.image?.url;

    if (!imageUrl) {
        return null;
    }

    // Map trust logos (filter out those without URL)
    const sliderImages: Array<HeroSliderImage> = data.trust_logos
        ? data.trust_logos.map(mapTrustLogoToSliderImage).filter((img): img is HeroSliderImage => img !== null)
        : [];

    // Link (only if url and text are present)
    const link =
        data.link?.url && data.link?.text
            ? {
                  url: data.link.url,
                  text: data.link.text,
                  target: data.link.target || "_self",
              }
            : undefined;

    return {
        imageUrl,
        imageAlt: data.image?.alt || "Hero image",
        titleHtml: data.title || "",
        heroText: data.hero_text,
        badgeIconUrl: data.badge_icon?.url,
        badgeIconAlt: data.badge_icon?.alt || "",
        sliderImages,
        link,
        anchorId: data.anchor_id,
    };
}
